// src/components/studentList/studentDetails/StudentFeeManagement.js

/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Typography,
    IconButton,
    Skeleton, // Import Skeleton
    Paper,
    Stack,
    Grid,
} from '@mui/material';
import {
    Save as SaveIcon,
    Edit as EditIcon,
    Close as CloseIcon,
    Payment as PaymentIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import MuiAlert from '@mui/material/Alert';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns'; // For date formatting

// Assuming these child components exist in the specified paths
import FeeSummary from './FeeSummary';
import FeeAssignmentTable from './FeeAssignmentTable.jsx';
import PaymentHistoryTable from './PaymentHistoryTable.jsx';
import StudentInfoHeader from '../StudentInfoHeader';
import axiosInstance from '../../../../../api/axiosConfig'; // Adjust path as necessary
import { STORAGE_KEYS } from '../../../../../constants';
import PageContainer from '../../../../common/PageContainer';
import PageHeader from '../../../../common/PageHeader';

// === Helper function for number to words (Simple English version) ===
// (Place this outside the component or in a utils file)
// === Helper function for number to words (CORRECTED English version) ===
// (Place this outside the component or in a utils file)
function numberToWords(num) {
    const a = [
        '', 'one ', 'two ', 'three ', 'four ', 'five ', 'six ', 'seven ', 'eight ', 'nine ', 'ten ', 'eleven ',
        'twelve ', 'thirteen ', 'fourteen ', 'fifteen ', 'sixteen ', 'seventeen ', 'eighteen ', 'nineteen '
    ];
    const b = [
        '', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'
    ];
    const g = [
        '', 'thousand', 'million', 'billion', 'trillion', 'quadrillion', 'quintillion' // Added more groups
    ];

    // --- Inner function to handle chunks less than 1000 ---
    function numToWordsChunk(n) {
        let str = '';
        const hundred = Math.floor(n / 100);
        const rest = n % 100;
        if (hundred) {
            str += a[hundred] + 'hundred ';
        }
        if (rest) {
            // Add 'and' only if there was a hundred part and the rest is non-zero
             str += (hundred && rest ? 'and ' : '') +
                    (rest < 20 ? a[rest] : b[Math.floor(rest / 10)] + (rest % 10 ? '-' + a[rest % 10] : ''));
        }
        return str;
    }

    // --- Main Function Logic ---

    // Handle non-numeric or null/undefined inputs
    if (num === null || num === undefined) return 'Invalid Number';
    const numericValue = Number(num); // Attempt to convert input to number
    if (isNaN(numericValue)) return 'Not a Number';

    // Handle Zero
    if (numericValue === 0) return 'Zero';

    const numStr = numericValue.toString();

    // --- Decimal Handling ---
    if (numStr.includes('.')) {
        const parts = numStr.split('.');
        const integerPart = parseInt(parts[0], 10);
        // Ensure decimal part handles cases like ".5" -> 50 or ".05" -> 5 properly
        const decimalStr = (parts[1] || '').padEnd(2, '0').substring(0, 2);
        const decimalPart = parseInt(decimalStr, 10);

        // Recursively call for integer and decimal parts (these will use the integer logic below)
        // Handle cases where integer or decimal part is zero
        let integerPartWords = (integerPart === 0) ? '' : numberToWords(integerPart);
        let decimalPartWords = (decimalPart === 0) ? '' : numberToWords(decimalPart);

        let finalWords = '';

        // Add "rupees" if integer part exists and is not zero
        if (integerPartWords && integerPartWords !== 'Zero') {
            finalWords += integerPartWords + ' rupees ';
        }

        // Add "paise" if decimal part exists and is not zero
        if (decimalPartWords && decimalPartWords !== 'Zero') {
            // Add 'and' if both parts exist and are non-zero
            finalWords += (integerPartWords && integerPartWords !== 'Zero' ? 'and ' : '') + decimalPartWords + ' paise ';
        }

        // Handle cases like 0.50 -> "Fifty paise"
        if (!finalWords && decimalPartWords && decimalPartWords !== 'Zero') {
             finalWords = decimalPartWords + 'paise ';
        }
        // Handle case 12.00 -> "Twelve rupees" (or just "Twelve" if you prefer)
         else if (integerPartWords && integerPartWords !== 'Zero' && !decimalPartWords) {
             finalWords = integerPartWords + ' rupees'; // Or adjust if you don't want 'rupees' for whole numbers
         }
        // Handle 0.00 -> "Zero rupees"
         else if (integerPart === 0 && decimalPart === 0) {
            return 'Zero rupees';
         }


        finalWords = finalWords.trim().replace(/\s+/g, ' ');
        // Capitalize only the very first letter
        return finalWords ? finalWords.charAt(0).toUpperCase() + finalWords.slice(1) : 'Zero'; // Return Zero if somehow empty

    } else {
        // --- Integer Handling (No Decimal) ---
        let words = '';
        let chunkCount = 0;
        let n = Math.abs(parseInt(numStr, 10)); // Work with absolute integer

        if (n === 0) return 'Zero'; // Should be caught by initial check, but safe

        while (n > 0) {
            const chunk = n % 1000;
            if (chunk) {
                // Add chunk words, group name (thousand, million, etc.), and separator space
                words = numToWordsChunk(chunk) + (g[chunkCount] ? g[chunkCount] + ' ' : '') + words;
            }
            n = Math.floor(n / 1000);
            chunkCount++;
        }

        // Clean up extra spaces and capitalize first letter
        words = words.trim().replace(/\s+/g, ' ');
        return words.charAt(0).toUpperCase() + words.slice(1);
    }
}


// === Styled Components ===
const StyledSectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '1.05rem',
    marginBottom: theme.spacing(1.5),
    color: theme.palette.text.primary,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    fontWeight: 600,
    margin: theme.spacing(1),
    textTransform: 'none', // Keep button text case as defined
    padding: theme.spacing(0.8, 2), // Adjust padding
}));

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const ActionButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    padding: theme.spacing(0.8, 2),
    [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(1, 0),
        width: '100%', // Stack buttons on small screens
    },
}));

const WorkflowCard = styled(Paper)(({ theme }) => ({
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(1.75),
    boxShadow: 'none',
    height: '100%',
}));

const SectionCard = styled(Paper)(({ theme }) => ({
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    boxShadow: 'none',
}));

// === Main Component ===
const StudentFeeManagement = () => {
    const { studentId } = useParams();
    // --- State Variables ---
    const [schoolId, setSchoolId] = useState(null);
    const [schoolDetails, setSchoolDetails] = useState({ // Initialize with placeholders
        name: 'Loading School Name...',
        address: 'Loading Address...',
        contact: 'Loading Contact...',
        logoUrl: '' // Default to no logo or a placeholder URL
    });
    const [feeTypes, setFeeTypes] = useState([]);
    const [fees, setFees] = useState([]); // Holds the *assigned* fee structure { feeTypeId, fees, discount }
    const [totalAssignedFee, setTotalAssignedFee] = useState(0);
    const [isEditingFees, setIsEditingFees] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [loading, setLoading] = useState(true);
    const [formErrors, setFormErrors] = useState({});
    const [selectedYear, setSelectedYear] = useState('');
    const [student, setStudent] = useState(null);
    const [paymentHistory, setPaymentHistory] = useState([]); // Holds the actual payment transactions
    const [totalPaidFee, setTotalPaidFee] = useState(0);
    const [pendingFee, setPendingFee] = useState(0);
    const [lastPaidFee, setLastPaidFee] = useState(null);

    // --- Snackbar Handler ---
    const showSnackbar = useCallback((message, severity) => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // --- Helper Functions ---
    const getSafe = useCallback((value, fallback = 'N/A') => value !== null && value !== undefined && value !== '' ? value : fallback, []);
    const getSafeNum = useCallback((value, decimals = 2, fallback = '-') => (typeof value === 'number' && !isNaN(value)) ? value.toFixed(decimals) : fallback, []);

    const formatCurrency = useCallback((value, currency = 'INR', locale = 'en-IN') => {
        try {
             if (typeof value !== 'number' || isNaN(value)) {
                 return `${currency} -`; // Handle non-numeric input gracefully
             }
            return new Intl.NumberFormat(locale, { style: 'currency', currency: currency }).format(value);
        } catch (e) {
            return `${currency} ${getSafeNum(value, 2, '0.00')}`;
        }
    }, [getSafeNum]);

    const formatDate = useCallback((dateString, formatString = 'PP') => { // PP -> Aug 16, 2023
        try {
            if (!dateString) return 'N/A';
            const date = new Date(dateString.split('T')[0]); // Use only the date part to avoid timezone issues
            if (isNaN(date.getTime())) { // Check if date is valid
                return 'Invalid Date';
            }
            return format(date, formatString);
        } catch (e) {
            console.error("Error formatting date:", e);
            return getSafe(dateString);
        }
    }, [getSafe]);

    // --- useEffect to set schoolId and retrieve school details from localStorage ---
    useEffect(() => {
        const storedSchoolId = localStorage.getItem('schoolId');
        if (storedSchoolId) {
            setSchoolId(Number(storedSchoolId));

            const email = localStorage.getItem(STORAGE_KEYS.SCHOOL_EMAIL);
            const phone = localStorage.getItem(STORAGE_KEYS.SCHOOL_PHONE);
        
                let contact = 'Contact Unavailable';
                if (email && phone) {
                    contact = `${email} | ${phone}`;
                } else if (email) {
                    contact = email;
                } else if (phone) {    
                    contact = phone;
                }
        
                const schoolDetails = {
                    name: localStorage.getItem('schoolName') || 'School Name Unavailable',
                    slogan: localStorage.getItem(STORAGE_KEYS.SCHOOL_SLOGAN) || null,
                    address: localStorage.getItem(STORAGE_KEYS.SCHOOL_ADDRESS) || 'Address Unavailable',
                    contact: contact,
                    logoUrl: localStorage.getItem(STORAGE_KEYS.SCHOOL_LOGO) || null
                };
                

            setSchoolDetails(schoolDetails);

        
        } else {
            showSnackbar('School ID not found in localStorage. Cannot load data.', 'error');
            setLoading(false); // Stop loading if school ID is missing
        }
    }, [showSnackbar, getSafe]); // Add getSafe to dependency array


    // --- Data Fetching Functions ---
    const fetchFeeTypes = useCallback(async (currentSchoolId) => {
        if (!currentSchoolId) return []; // Don't fetch if schoolId is not set
        try {
            const response = await axiosInstance.get(`/api/fees/types/school/${currentSchoolId}`);
            if (response.data.length === 0) {
                showSnackbar('No fee types configured for this school.', 'warning');
            }
            const fetchedFeeTypes = response.data.map((feeType) => ({
                ...feeType,
                feeTypeId: Number(feeType.feeTypeId), // Ensure numeric ID
                status: feeType.status === 'Active' || feeType.status === true, // Normalize status
            }));
            setFeeTypes(fetchedFeeTypes);
            return fetchedFeeTypes; // Return for sequential fetching
        } catch (error) {
            console.error('Error fetching fee types:', error);
            showSnackbar('Error fetching fee types.', 'error');
            return []; // Return empty array on error
        }
    }, [showSnackbar]); // Removed axiosInstance dependency as it's stable

    const fetchStudentDataAndFees = useCallback(async (currentFeeTypes, currentSchoolId) => {
        if (!studentId || !currentSchoolId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            // Fetch student details, payment history, and assigned fee structure in parallel
            const [studentResponse, paymentHistoryResponse, assignedFeesResponse] = await Promise.all([
                axiosInstance.get(`/api/students/${studentId}`),
                axiosInstance.get(`/api/fees/student/${studentId}`), // This endpoint gets payment records
                axiosInstance.get(`/api/student-fees/student/${studentId}`) // This gets the assigned structure
            ]);

            // --- Process Student Data ---
            const studentData = studentResponse.data;
            if (!studentData) {
                throw new Error("Student data not found");
            }
            setStudent(studentData);
            const currentAcademicYear = studentData.academicYear;
            setSelectedYear(currentAcademicYear); // Set academic year from student data

            // --- Process Payment History ---
            const paymentData = paymentHistoryResponse.data || []; // Ensure it's an array
            setPaymentHistory(paymentData);
            const paidAmount = paymentData.reduce((acc, payment) => acc + (parseFloat(payment.amount) || 0), 0);
            setTotalPaidFee(paidAmount);

            if (paymentData.length > 0) {
                // Sort by payment date descending to find the last payment
                const sortedPayments = [...paymentData].sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));
                setLastPaidFee(sortedPayments[0]);
            } else {
                setLastPaidFee(null);
            }

            // --- Process Assigned Fee Structure ---
            let feeBreakupArray = [];
            const assignedFeeData = assignedFeesResponse.data || []; // Ensure it's an array

            if (assignedFeeData.length > 0) {
                // Find the fee structure for the student's *current* academic year
                const currentYearFeeData = assignedFeeData.find(feeData => feeData.academicYear === currentAcademicYear);
                if (currentYearFeeData && currentYearFeeData.feeBreakup) {
                    try {
                        // Safely parse the feeBreakup JSON string
                        feeBreakupArray = JSON.parse(currentYearFeeData.feeBreakup);
                        if (!Array.isArray(feeBreakupArray)) feeBreakupArray = []; // Ensure it's an array
                    } catch (parseError) {
                        console.error('Error parsing feeBreakup JSON:', parseError);
                        showSnackbar('Failed to parse assigned fee data.', 'error');
                        feeBreakupArray = []; // Reset on error
                    }
                } else {
                     console.log(`No assigned fee structure found for student ${studentId} in year ${currentAcademicYear}.`);
                     // No specific snackbar needed here unless it's unexpected
                }
            }

            // --- Initialize `fees` state based on fetched Fee Types and Assigned Data ---
            const initializedFees = currentFeeTypes.map((feeType) => {
                // Find if this fee type exists in the parsed assigned data
                const assignedFee = feeBreakupArray.find(f => Number(f.feeTypeId) === feeType.feeTypeId);

                return {
                    feeTypeId: feeType.feeTypeId,
                    fees: assignedFee ? getSafe(assignedFee.fees, '0').toString() : '0',
                    discount: assignedFee ? getSafe(assignedFee.discount, '0').toString() : '0',
                };
            });

            setFees(initializedFees); // Update state with initialized/fetched values

            // --- Calculate Totals based on the initialized/fetched `fees` state ---
            const calculatedTotalAssigned = initializedFees.reduce((sum, fee) => {
                const feeAmount = parseFloat(fee.fees) || 0;
                const discount = parseFloat(fee.discount) || 0;
                // Ensure discount doesn't make the effective fee negative
                const effectiveFee = Math.max(feeAmount - discount, 0);
                return sum + effectiveFee;
            }, 0);

            setTotalAssignedFee(calculatedTotalAssigned);
            setPendingFee(Math.max(calculatedTotalAssigned - paidAmount, 0)); // Recalculate pending

        } catch (error) {
            console.error('Error fetching student data and fees:', error);
             if (error.response) {
                 console.error("Error response data:", error.response.data);
                 console.error("Error response status:", error.response.status);
             } else if (error.request) {
                 console.error("Error request:", error.request);
             } else {
                 console.error("Error message:", error.message);
             }
            showSnackbar(`Error fetching data: ${error.message || 'Check console'}`, 'error');
            // Reset states on error? Optional, depends on desired behavior.
            // setStudent(null);
            // setFees([]);
            // setPaymentHistory([]);
        } finally {
            setLoading(false);
        }
    }, [studentId, showSnackbar, getSafe]); // Dependencies

     // --- useEffect Chain for Data Fetching ---
     useEffect(() => {
         if (schoolId) {
             const fetchDataSequentially = async () => {
                 setLoading(true); // Start loading indicator
                 const fetchedFeeTypes = await fetchFeeTypes(schoolId);
                 // Only fetch student data if fee types were fetched (or if no fee types exist but we still need student info)
                 // and if studentId is valid
                 if (studentId) {
                      // Pass the fetched fee types directly
                     await fetchStudentDataAndFees(fetchedFeeTypes, schoolId);
                 } else {
                     setLoading(false); // Stop loading if no studentId
                 }
             };
             fetchDataSequentially();
         }
         // Intentionally not including fetchFeeTypes and fetchStudentDataAndFees in deps
         // to avoid re-running the entire chain when they are redefined.
         // schoolId and studentId are the primary triggers.
     }, [schoolId, studentId]);


    // --- Fee Assignment Handlers ---
    const handleFeeInputChange = (feeTypeId, field, value) => {
        if (feeTypeId === undefined || feeTypeId === null) {
            console.error('Invalid feeTypeId passed to handleFeeInputChange');
            return;
        }

        // Basic validation: allow empty string or numbers (including decimals)
        const isValidInput = value === '' || /^\d*\.?\d*$/.test(value);

        if (!isValidInput) {
            setFormErrors((prevErrors) => ({
                ...prevErrors,
                [`${feeTypeId}_${field}`]: `Invalid number format.`,
            }));
            // Optionally update the state with the invalid value to show it, or revert
             setFees((prevFees) =>
                 prevFees.map((fee) =>
                     fee.feeTypeId === feeTypeId ? { ...fee, [field]: value } : fee
                 )
             );
            return;
        } else {
             // Clear the specific error if input becomes valid
            setFormErrors((prevErrors) => {
                const { [`${feeTypeId}_${field}`]: removedError, ...rest } = prevErrors;
                return rest;
            });
        }

        setFees((prevFees) => {
            const updatedFees = prevFees.map((fee) =>
                fee.feeTypeId === feeTypeId ? { ...fee, [field]: value } : fee
            );
            // Recalculate totals immediately on valid input change
            calculateTotalAssignedFee(updatedFees);
            return updatedFees;
        });
    };

    // Separated calculation logic
    const calculateTotalAssignedFee = (feesList) => {
        const total = feesList.reduce((sum, fee) => {
            const feeAmount = parseFloat(fee.fees) || 0;
            const discount = parseFloat(fee.discount) || 0;
            const discountedFee = Math.max(feeAmount - discount, 0); // Ensure non-negative
            return sum + discountedFee;
        }, 0);
        setTotalAssignedFee(total);
        // Also update pending fee whenever assigned fee changes
        setPendingFee(Math.max(total - totalPaidFee, 0));
    };


    const handleEditFeesToggle = () => {
        setIsEditingFees(!isEditingFees);
        if (isEditingFees) { // If toggling OFF editing
            setFormErrors({}); // Clear errors when canceling edit
            // Optional: Refetch data to revert changes if needed, but might be slow.
            // if (schoolId && studentId) {
            //     fetchStudentDataAndFees(feeTypes, schoolId);
            // }
        }
    };

    const validateFeeForm = () => {
        const errors = {};
        let isValid = true;

        fees.forEach((fee) => {
            const feeAmountStr = fee.fees || '';
            const discountAmountStr = fee.discount || '';
            const feeAmount = parseFloat(feeAmountStr);
            const discountAmount = parseFloat(discountAmountStr);

            // Check if fees input is valid number >= 0
            if (feeAmountStr === '' || isNaN(feeAmount) || feeAmount < 0) {
                errors[`${fee.feeTypeId}_fees`] = 'Valid amount required (>= 0).';
                isValid = false;
            }
            // Check if discount input is valid number >= 0
            if (discountAmountStr === '' || isNaN(discountAmount) || discountAmount < 0) {
                 errors[`${fee.feeTypeId}_discount`] = 'Valid discount required (>= 0).';
                 isValid = false;
            // Check if discount exceeds fee amount (only if both are valid numbers)
            } else if (!isNaN(feeAmount) && feeAmount >= 0 && discountAmount > feeAmount) {
                errors[`${fee.feeTypeId}_discount`] = 'Discount cannot exceed fee.';
                isValid = false;
            }
        });

        // Check overall total (though individual checks mostly cover this)
        if (totalAssignedFee < 0) {
            // This case should ideally be prevented by Math.max in calculations
            errors.totalAssignedFee = 'Total assigned fee is negative. Please check inputs.';
             isValid = false;
        }

        setFormErrors(errors);
        return isValid;
    };

    const handleSaveFees = () => {
        if (validateFeeForm()) {
            setConfirmationOpen(true); // Open confirmation dialog if valid
        } else {
            showSnackbar('Please correct the highlighted errors in fee assignments.', 'error');
        }
    };

    const confirmSaveFees = async () => {
        setConfirmationOpen(false); // Close dialog immediately
        setLoading(true);
        try {
            // Prepare data in the required format for the API
            const feesToSave = fees.map((fee) => ({
                feeTypeId: fee.feeTypeId,
                // Ensure values are numbers, default to 0 if parsing fails
                fees: parseFloat(fee.fees) || 0,
                discount: parseFloat(fee.discount) || 0,
            }));

            const updatedFeeBreakup = JSON.stringify(feesToSave); // Convert to JSON string

            // Check if fee data for this student and year already exists
            const existingFeeDataResponse = await axiosInstance.get(`/api/student-fees/student/${studentId}?academicYear=${selectedYear}`);

             let existingRecord = null;
             if (existingFeeDataResponse.data && Array.isArray(existingFeeDataResponse.data)) {
                  existingRecord = existingFeeDataResponse.data.find(d => d.academicYear === selectedYear);
             }


            const userId = 1; // TODO: Replace with actual logged-in user ID

            if (existingRecord && existingRecord.id) {
                // --- UPDATE existing record ---
                const studentFeeDataDTO = {
                    id: existingRecord.id, // Include the ID for PUT request
                    studentId: Number(studentId),
                    academicYear: selectedYear,
                    feeBreakup: updatedFeeBreakup,
                    updatedBy: userId, // Add updatedBy field
                    // Include other fields from existingRecord if necessary and if the PUT expects the full object
                     schoolId: existingRecord.schoolId, // Assuming these exist and might be needed
                     createdBy: existingRecord.createdBy,
                     createdAt: existingRecord.createdAt, // Often backend handles timestamps
                };
                 console.log("Updating fee assignment:", studentFeeDataDTO);
                await axiosInstance.put(`/api/student-fees/${existingRecord.id}`, studentFeeDataDTO);
                showSnackbar('Fee assignments updated successfully!', 'success');

            } else {
                // --- CREATE new record ---
                const studentFeeDataDTO = {
                    studentId: Number(studentId),
                    schoolId: Number(schoolId), // Include schoolId for POST
                    academicYear: selectedYear,
                    feeBreakup: updatedFeeBreakup,
                    createdBy: userId, // Add createdBy field
                    updatedBy: userId, // Add updatedBy field
                };
                console.log("Creating new fee assignment:", studentFeeDataDTO);
                await axiosInstance.post(`/api/student-fees`, studentFeeDataDTO);
                showSnackbar('Fee assignments saved successfully!', 'success');
            }

            setIsEditingFees(false); // Exit editing mode
            // Refetch data to show the saved state and recalculate totals
            // Need feeTypes which should already be in state
             await fetchStudentDataAndFees(feeTypes, schoolId);

        } catch (error) {
            console.error('Error saving fee assignments:', error);
             if (error.response) {
                 console.error("Save error response data:", error.response.data);
                 const errorMsg = error.response.data?.message || error.response.data?.error || 'Failed to save fee assignments.';
                 showSnackbar(`Error: ${errorMsg}`, 'error');
             } else {
                 showSnackbar('Failed to save fee assignments. Check network or console.', 'error');
             }
        } finally {
            setLoading(false);
        }
    };

    // --- Payment History and Report Handlers ---

    const handlePrintReceipt = (fee) => {
        if (!fee || !student || !schoolDetails) {
            showSnackbar('Cannot generate receipt: Missing required data.', 'error');
            console.error("Missing data for receipt:", { fee, student, schoolDetails });
            return;
        }

        console.log("Generating Receipt for Fee ID:", fee.feeId);

        // --- Data Extraction and Formatting ---
        const studentName = `${getSafe(student.firstName)} ${getSafe(student.lastName)}`;
        const admissionNo = getSafe(student.admissionNumber);
        const classSectionString = `${getSafe(student.classes?.className, '')}${student.section?.sectionName ? ` / ${getSafe(student.section.sectionName)}` : ''}` || 'N/A';
        const schoolName = getSafe(schoolDetails.name, 'School Name');
        const schoolAddress = getSafe(schoolDetails.address, 'School Address');
        const schoolContact = getSafe(schoolDetails.contact, 'School Contact');
        const schoolLogoUrl = schoolDetails.logoUrl || ''; // Handle potentially missing logo

        const receiptNo = `R${String(fee.feeId).padStart(5, '0')}`; // Example: R00012
        const paymentDate = formatDate(fee.paymentDate, 'dd MMM yyyy'); // Format: 18 Mar 2025
        const academicYear = getSafe(fee.academicYear, selectedYear); // Use fee's year, fallback to selectedYear
        const amountPaid = parseFloat(fee.amount) || 0;
        const amountInWords = numberToWords(amountPaid); // Use the helper function
        const paymentMode = getSafe(fee.paymentMode);
        const referenceNumber = getSafe(fee.referenceNumber, '-'); // Show '-' if empty
        const comments = getSafe(fee.comments, '-');

        // Use state values for overall summary
        const totalFeeAssignedFormatted = formatCurrency(totalAssignedFee);
        const totalPaidFormatted = formatCurrency(totalPaidFee);
        const pendingFeeFormatted = formatCurrency(pendingFee);

        // --- HTML Template (A5 Landscape) ---
        const receiptHtml = `
          <!DOCTYPE html>
          <html lang="en">
          <head>
              <meta charset="UTF-8">
              <title>Fee Receipt - ${studentName} - ${receiptNo}</title>
              <link rel="preconnect" href="https://fonts.googleapis.com">
              <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
              <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
              <style>
                  @page { size: A5 landscape; margin: 8mm; }
                  body {
                      margin: 0; padding: 0; font-family: 'Inter', 'Manrope', 'Plus Jakarta Sans', sans-serif; font-size: 9pt;
                      line-height: 1.4; color: #333; background-color: #fff;
                      -webkit-print-color-adjust: exact; print-color-adjust: exact;
                  }
                  .receipt-print-container {
                      width: 194mm; /* A5 landscape width - 2*margin */
                      height: 132mm; /* A5 landscape height - 2*margin */
                      margin: 0 auto; box-sizing: border-box; display: flex; flex-direction: column;
                      border: 1px solid #ccc; border-radius: 5px; overflow: hidden;
                  }
                  .receipt-content { padding: 6mm; flex-grow: 1; display: flex; flex-direction: column; }
                  .receipt-header, .receipt-title, .student-info-section, .payment-details-section,
                  .summary-section, .notes-section, .receipt-footer-section {
                      break-inside: avoid; page-break-inside: avoid; margin-bottom: 3mm; /* Spacing between sections */
                  }
                  /* --- Header --- */
                  .receipt-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1.5px solid #4a90e2; padding-bottom: 3mm; margin-bottom: 4mm; }
                  .school-info { flex-grow: 1; padding-right: 5mm; }
                  .school-info h1 { margin: 0 0 1mm 0; font-size: 12pt; color: #004085; font-weight: 600; }
                  .school-info p { margin: 0.5mm 0; font-size: 8pt; color: #444; line-height: 1.3; }
                  .school-logo img { max-height: 35px; max-width: 110px; object-fit: contain; }
                  /* --- Title --- */
                  .receipt-title { text-align: center; margin-bottom: 4mm; }
                  .receipt-title h2 { margin: 0; font-size: 11pt; color: #047857; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
                   .receipt-copy { font-size: 7.5pt; color: #666; margin-top: 0.5mm; }
                  /* --- Student Info --- */
                  .student-info-section table { width: 100%; border-collapse: collapse; font-size: 8.5pt; margin-bottom: 3mm;}
                  .student-info-section td { padding: 1mm 1.5mm; vertical-align: top; }
                  .student-info-section .label { font-weight: 500; color: #555; width: 18%; white-space: nowrap; }
                  .student-info-section .value { font-weight: 600; color: #111; width: 32%; }
                  /* --- Payment Details --- */
                  .payment-details-section { border-top: 1px dashed #ccc; border-bottom: 1px dashed #ccc; padding: 3mm 0; }
                  .payment-details-section table { width: 100%; border-collapse: collapse; font-size: 9pt;}
                  .payment-details-section td { padding: 1.5mm 1.5mm; }
                  .payment-details-section .label { font-weight: 500; color: #555; width: 25%; }
                  .payment-details-section .value { font-weight: 600; color: #111; width: 75%; }
                  .amount-paid-row .label { font-size: 10pt; }
                  .amount-paid-row .value { font-size: 10pt; color: #0056b3; }
                  .amount-words { font-size: 8pt; font-style: italic; color: #444; }
                  /* --- Summary --- */
                  .summary-section { background-color: #f8f9fa; padding: 2.5mm; border-radius: 3px; }
                  .summary-section table { width: 100%; font-size: 8.5pt; }
                  .summary-section td { padding: 1mm 1.5mm; }
                  .summary-section .label { text-align: right; font-weight: 500; color: #555; width: 70%; padding-right: 2mm;}
                  .summary-section .value { font-weight: 600; color: #333; width: 30%; }
                  .summary-section .pending { color: #dc3545; } /* Red for pending */
                  /* --- Notes --- */
                  .notes-section { font-size: 8pt; color: #666; margin-top: 2mm;}
                  /* --- Footer --- */
                  .receipt-footer-section { margin-top: auto; padding-top: 4mm; border-top: 1px solid #eee; }
                  .receipt-footer { display: flex; justify-content: space-between; align-items: flex-end; font-size: 8pt; color: #555; }
                  .signature-area { text-align: center; }
                  .signature-line { border-bottom: 1px dotted #888; width: 50mm; margin: 10mm auto 1mm auto; display: block; }
                   .thank-you { text-align: center; font-size: 8pt; margin-top: 3mm; color: #777; }
                   .print-date { text-align: left; }
              </style>
          </head>
          <body>
              <div class="receipt-print-container">
                  <div class="receipt-content">
                      <header class="receipt-header">
                          <div class="school-info">
                              <h1>${schoolName}</h1>
                              <p>${schoolAddress}</p>
                              <p>${schoolContact}</p>
                          </div>
                          ${schoolLogoUrl ? `<div class="school-logo"><img src="${schoolLogoUrl}" alt="Logo"></div>` : ''}
                      </header>

                      <section class="receipt-title">
                          <h2>Fee Payment Receipt</h2>
                           <div class="receipt-copy">(Student Copy)</div>
                      </section>

                      <section class="student-info-section">
                          <table>
                              <tr>
                                  <td class="label">Receipt No:</td><td class="value">${receiptNo}</td>
                                  <td class="label">Payment Date:</td><td class="value">${paymentDate}</td>
                              </tr>
                              <tr>
                                  <td class="label">Student Name:</td><td class="value">${studentName}</td>
                                  <td class="label">Admission No:</td><td class="value">${admissionNo}</td>
                              </tr>
                              <tr>
                                  <td class="label">Class / Section:</td><td class="value">${classSectionString}</td>
                                  <td class="label">Academic Year:</td><td class="value">${academicYear}</td>
                              </tr>
                          </table>
                      </section>

                      <section class="payment-details-section">
                          <table>
                              <tr class="amount-paid-row">
                                  <td class="label">Amount Paid:</td>
                                  <td class="value">${formatCurrency(amountPaid)}</td>
                              </tr>
                              <tr>
                                  <td class="label">Amount in Words:</td>
                                  <td class="value amount-words">${amountInWords}</td>
                              </tr>
                              <tr>
                                  <td class="label">Payment Mode:</td><td class="value">${paymentMode} ${referenceNumber !== '-' ? `(Ref: ${referenceNumber})` : ''}</td>
                              </tr>
                          </table>
                      </section>

                       <section class="summary-section">
                           <table>
                               <tr>
                                   <td class="label">Total Assigned Fee (Current Year):</td>
                                   <td class="value">${totalFeeAssignedFormatted}</td>
                               </tr>
                               <tr>
                                   <td class="label">Total Paid (Including this payment):</td>
                                   <td class="value">${totalPaidFormatted}</td>
                               </tr>
                               <tr>
                                   <td class="label">Balance Due (After this payment):</td>
                                   <td class="value pending">${pendingFeeFormatted}</td>
                               </tr>
                           </table>
                       </section>

                       ${comments !== '-' ? `
                       <section class="notes-section">
                           <strong>Comments:</strong> ${comments}
                       </section>
                       ` : ''}


                      <section class="receipt-footer-section">
                          <div class="receipt-footer">
                               <div class="print-date">Printed On: ${formatDate(new Date(), 'dd MMM yyyy, hh:mm a')}</div>
                              <div class="signature-area">
                                  <span class="signature-line"></span>
                                  Received By (Signature & Stamp)
                              </div>
                          </div>
                           <div class="thank-you">Thank you for your payment!</div>
                      </section>
                  </div>
              </div>
          </body>
          </html>
        `;

        // --- Open HTML in a new window and print ---
        const printWindow = window.open('', '_blank', 'height=600,width=1000');
        if (printWindow) {
            printWindow.document.write(receiptHtml);
            printWindow.document.close(); // Necessary for IE/Edge
            printWindow.focus(); // Focus on the new window
            // Timeout allows content to render before print dialog
            setTimeout(() => {
                 printWindow.print();
                // Optional: Close window after print dialog is handled (or cancelled)
                 // Be cautious with auto-closing popups as browsers might block it.
                 // setTimeout(() => { printWindow.close(); }, 1000);
            }, 500); // 500ms delay
        } else {
            showSnackbar('Could not open print window. Please check popup blocker settings.', 'warning');
        }
    };


    const handleDownloadFeeReport = async () => {
         if (!studentId) {
             showSnackbar('Student ID is missing, cannot download report.', 'error');
             return;
         }
        setLoading(true); // Indicate activity
        try {
            const response = await axiosInstance.get(
                `/api/school/reports/fees/${studentId}`, // Ensure this endpoint exists and generates PDF
                {
                    responseType: 'blob', // Important for file download
                }
            );

             // Check if the response is actually a blob (might be JSON error)
             if (response.data.type === 'application/pdf') {
                 const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                 const link = document.createElement('a');
                 link.href = url;
                 // Suggest a filename
                 const filename = `FeeReport_${student?.admissionNumber || studentId}_${selectedYear}.pdf`;
                 link.setAttribute('download', filename);
                 document.body.appendChild(link);
                 link.click();

                 // Clean up the object URL
                 link.parentNode.removeChild(link);
                 window.URL.revokeObjectURL(url);

                 showSnackbar('Fees report download initiated.', 'success');
             } else {
                  // Handle potential JSON error response wrapped in blob
                  const reader = new FileReader();
                  reader.onload = function() {
                      try {
                         const errorJson = JSON.parse(reader.result);
                         console.error('Error downloading PDF:', errorJson);
                         showSnackbar(`Failed to generate report: ${errorJson.message || 'Server error'}`, 'error');
                      } catch (e) {
                           console.error('Error downloading PDF: Response is not PDF or valid JSON.');
                          showSnackbar('Error downloading fees report: Invalid response from server.', 'error');
                      }
                  }
                  reader.readAsText(response.data);
             }
        } catch (error) {
            console.error('Error downloading fees report:', error);
             if (error.response && error.response.data) {
                  // Try to read error from blob response if possible
                 const reader = new FileReader();
                 reader.onload = function() {
                     try {
                         const errorJson = JSON.parse(reader.result);
                         showSnackbar(`Download error: ${errorJson.message || 'Server error'}`, 'error');
                     } catch (e) {
                         showSnackbar('Error downloading report. Check console.', 'error');
                     }
                 }
                 reader.readAsText(error.response.data);

             } else {
                 showSnackbar('Error downloading fees report. Check network or console.', 'error');
             }
        } finally {
            setLoading(false); // Stop indication
        }
    };

    // --- Render Loading and Error States ---
    // Enhanced Skeleton Loading
    const renderSkeletons = () => (
        <PageContainer>
            <Skeleton animation="wave" height={36} width="32%" style={{ marginBottom: 10 }} />
            <Skeleton animation="wave" height={20} width="48%" style={{ marginBottom: 16 }} />
            <Skeleton animation="wave" variant="rectangular" height={88} width="100%" style={{ marginBottom: 16, borderRadius: 6 }} />
            <Skeleton animation="wave" variant="rectangular" height={104} width="100%" style={{ marginBottom: 16, borderRadius: 6 }} />
            <Skeleton animation="wave" variant="rectangular" height={240} width="100%" style={{ marginBottom: 16, borderRadius: 6 }} />
            <Skeleton animation="wave" variant="rectangular" height={220} width="100%" style={{ borderRadius: 6 }} />
        </PageContainer>
    );

    if (loading && !student) { // Show skeleton only during initial load
        return renderSkeletons();
    }

    if (!schoolId) {
         return (
             <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                 <Typography variant="h6" color="error">School ID not configured. Cannot display fee data.</Typography>
             </Box>
         );
    }

    if (!loading && !student && studentId) { // If loading finished but student not found
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="80vh">
                <Typography variant="h6" color="error">Student data could not be loaded for ID: {studentId}.</Typography>
            </Box>
        );
    }


    // === Render Component ===
    return (
        <PageContainer>
                <PageHeader
                    title="Student Fees"
                    subtitle={student ? `${getSafe(student.firstName)} ${getSafe(student.lastName)} • ${getSafe(student.admissionNumber)} • ${getSafe(student.classes?.className)}${student.section?.sectionName ? ` / ${getSafe(student.section.sectionName)}` : ''}` : `Student ID: ${studentId}`}
                    actions={(
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            {!isEditingFees ? (
                                <>
                                    <ActionButton
                                        variant="outlined"
                                        color="secondary"
                                        startIcon={<EditIcon />}
                                        onClick={handleEditFeesToggle}
                                        disabled={loading || feeTypes.length === 0}
                                    >
                                        Edit Fee
                                    </ActionButton>
                                    <ActionButton
                                        component={Link}
                                        to={`/fees/create?studentId=${studentId}&academicYear=${selectedYear}&pending=${pendingFee}`}
                                        color="primary"
                                        variant="contained"
                                        startIcon={<PaymentIcon />}
                                        disabled={loading || !student}
                                    >
                                        Add Payment
                                    </ActionButton>
                                    <ActionButton
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadFeeReport}
                                        disabled={loading || !student}
                                    >
                                        Download Report
                                    </ActionButton>
                                </>
                            ) : (
                                <>
                                    <ActionButton
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveFees}
                                        disabled={loading || Object.keys(formErrors).length > 0}
                                    >
                                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Save Fees'}
                                    </ActionButton>
                                    <ActionButton
                                        variant="outlined"
                                        color="inherit"
                                        onClick={handleEditFeesToggle}
                                        disabled={loading}
                                    >
                                        Cancel
                                    </ActionButton>
                                </>
                            )}
                        </Stack>
                    )}
                />

                <Stack spacing={2}>
                    <Paper variant="outlined" sx={{ borderRadius: 1.5, p: 2 }}>
                        {student && <StudentInfoHeader student={student} />}

                        <Grid container spacing={1.5} sx={{ mt: 0.5, mb: 0.5 }}>
                            {[
                                {
                                    title: 'Step 1 · Assign fee',
                                    description: 'Use the fee structure section to set yearly amounts and discounts.',
                                },
                                {
                                    title: 'Step 2 · Post payment',
                                    description: 'Open the payment form to record amount, mode, and reference.',
                                },
                                {
                                    title: 'Step 3 · Track balance',
                                    description: 'Use summary cards and payment history to follow pending dues.',
                                },
                            ].map((item) => (
                                <Grid item xs={12} md={4} key={item.title}>
                                    <WorkflowCard>
                                        <Typography variant="body2" sx={{ fontWeight: 800, mb: 0.4 }}>
                                            {item.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {item.description}
                                        </Typography>
                                    </WorkflowCard>
                                </Grid>
                            ))}
                        </Grid>

                        <FeeSummary
                            totalAssignedFee={totalAssignedFee}
                            totalPaidFee={totalPaidFee}
                            pendingFee={pendingFee}
                            lastPaidFee={lastPaidFee}
                            formatCurrency={formatCurrency}
                            formatDate={formatDate}
                            loading={loading && !student}
                        />
                    </Paper>

                    <SectionCard>
                        <StyledSectionTitle variant="h6">Step 1 · Assign Fee</StyledSectionTitle>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Academic year: {selectedYear || 'N/A'} · Set the yearly fee once, then save.
                        </Typography>
                        {loading && !fees.length ? (
                            <Skeleton animation="wave" variant="rectangular" height={200} width="100%" style={{ marginBottom: 16, borderRadius: 6 }} />
                        ) : feeTypes.length === 0 && !isEditingFees ? (
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5, bgcolor: 'grey.50' }}>
                                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                                    No fee types found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Create fee types first, then come back here to assign the fee structure.
                                </Typography>
                            </Paper>
                        ) : (
                            <FeeAssignmentTable
                                feeTypes={feeTypes}
                                fees={fees}
                                isEditingFees={isEditingFees}
                                formErrors={formErrors}
                                handleFeeInputChange={handleFeeInputChange}
                                totalAssignedFee={totalAssignedFee}
                                formatCurrency={formatCurrency}
                            />
                        )}
                    </SectionCard>

                    <SectionCard>
                        <StyledSectionTitle variant="h6">Step 2 · Payment History</StyledSectionTitle>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                            Every posted payment appears here with receipt, mode, reference, and running balance.
                        </Typography>
                        {loading && !paymentHistory.length ? (
                            <Skeleton animation="wave" variant="rectangular" height={200} width="100%" style={{ borderRadius: 6 }} />
                        ) : (
                            <PaymentHistoryTable
                                paymentHistory={paymentHistory}
                                handlePrintReceipt={handlePrintReceipt}
                                formatCurrency={formatCurrency}
                                studentName={student ? `${getSafe(student.firstName)} ${getSafe(student.lastName)}` : 'Student'}
                                schoolDetails={schoolDetails}
                                studentDetails={{
                                    admissionNo: getSafe(student?.admissionNumber),
                                    classSection: `${getSafe(student?.classes?.className, '')}${student?.section?.sectionName ? ` / ${getSafe(student.section.sectionName)}` : ''}` || 'N/A',
                                    academicYear: selectedYear,
                                }}
                                feeSummaryDetails={{
                                    totalAssigned: totalAssignedFee,
                                    totalPaid: totalPaidFee,
                                    balanceDue: pendingFee,
                                }}
                            />
                        )}
                    </SectionCard>
                </Stack>

                {/* Confirmation Dialog for Saving Fees */}
                <Dialog open={confirmationOpen} onClose={() => !loading && setConfirmationOpen(false)}>
                    <DialogTitle>Confirm Save Fee Assignments</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to save these fee assignments for {student ? `${student.firstName} ${student.lastName}` : `student ${studentId}`} for the academic year {selectedYear}?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <StyledButton onClick={() => setConfirmationOpen(false)} color="primary" disabled={loading}>
                            Cancel
                        </StyledButton>
                        <StyledButton onClick={confirmSaveFees} color="primary" variant="contained" disabled={loading}>
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Confirm & Save'}
                        </StyledButton>
                    </DialogActions>
                </Dialog>

                {/* Snackbar for Notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleSnackbarClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleSnackbarClose}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={handleSnackbarClose}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

        </PageContainer>
    );
};

export default StudentFeeManagement;
